import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, FileText, Loader2, ChevronRight, CheckCircle2, History } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getMyResourceContext,
  listAccessibleTemplates,
  type DocumentTemplate,
} from '../../lib/documents';
import DocumentFillModal from './DocumentFillModal';

interface DocumentPickerModalProps {
  businessId: string;
  onClose: () => void;
}

export default function DocumentPickerModal({ businessId, onClose }: DocumentPickerModalProps) {
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [fillId, setFillId] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const ctx = await getMyResourceContext(businessId);
        const data = await listAccessibleTemplates(businessId, ctx.resourceId, ctx.roleId);
        if (active) setTemplates(data);
      } catch (e) {
        console.error('Failed to load documents:', e);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [businessId]);

  const hasDraft = (id: string) => {
    try {
      const raw = localStorage.getItem(`doc_fill_draft_v1:${id}`);
      if (!raw) return false;
      const d = JSON.parse(raw);
      return !!d && ((d.values && Object.keys(d.values).length > 0) || (d.signatures && Object.keys(d.signatures).length > 0) || d.consent === true);
    } catch { return false; }
  };

  const filtered = templates.filter((t) =>
    !search.trim() || t.title.toLowerCase().includes(search.toLowerCase()) || (t.tags || []).some((x) => x.toLowerCase().includes(search.toLowerCase())),
  );

  const content = (
    <div className="fixed inset-0 z-[100040] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-[16px] font-semibold text-gray-900">Complete a document</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"><X className="h-5 w-5" /></button>
        </div>

        <div className="px-5 py-3 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search documents…" className="w-full pl-9 pr-3 py-2.5 bg-gray-50 rounded-xl text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500/30" style={{ fontSize: '16px' }} />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {loading ? (
            <div className="flex justify-center py-12 text-gray-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-9 w-9 text-gray-300 mx-auto mb-2" />
              <div className="text-[14px] text-gray-600 font-medium">No documents available</div>
              <div className="text-[12px] text-gray-400">Nothing is assigned to you or public yet.</div>
            </div>
          ) : (
            <div className="space-y-1.5">
              {filtered.map((t) => (
                <button key={t.id} onClick={() => setFillId(t.id)} className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-blue-50 text-left transition-colors group">
                  <div className="p-2 rounded-lg bg-blue-50 group-hover:bg-blue-100"><FileText className="h-5 w-5 text-blue-600" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[14px] font-semibold text-gray-900 truncate">{t.title}</span>
                      {hasDraft(t.id) && <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded flex-shrink-0"><History className="h-2.5 w-2.5" /> In progress</span>}
                    </div>
                    {t.description && <div className="text-[12px] text-gray-500 truncate">{t.description}</div>}
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-blue-500" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {fillId && (
        <DocumentFillModal
          templateId={fillId}
          onClose={() => setFillId(null)}
          onCompleted={() => {
            toast.success('Document completed ✓', { icon: <CheckCircle2 className="h-5 w-5 text-green-500" /> as any });
            setFillId(null);
            onClose();
          }}
        />
      )}
    </div>
  );

  return createPortal(content, document.body);
}
